import {
	createDesignDefinition,
	importDesignPreset,
	planDesignPresetBinding,
	type DesignDefinition,
	type DesignPresetStyles,
} from 'common/design-tokens';
import {
	createFontResolver,
	canonicalFontFamily,
	type FontResolver,
	type ResolvedFont,
} from 'admin/components/common/AI/Chat/fonts/font-resolver';
import { registerResolvedFonts } from 'admin/components/pages/Story/StoryEditorChat/tools/update-project/register-resolved-fonts';
import type { StoryMediaQuery, StorySettingsType } from 'types/story';

export function validateDesignPresetFontFace(
	font: ResolvedFont | undefined,
	family: string,
	weight?: number
): ResolvedFont {
	if (!font || font.source === 'google' || (font.source === 'organization' && !font.faces?.length))
		throw new Error(`Font family "${family}" is not registered in this Story or organization.`);
	if (weight !== undefined && font.source !== 'system') {
		const weights = (font.faces ?? []).flatMap(face =>
			face.fontType === 'custom'
				? [face.weight]
				: (face.variants ?? []).map(variant =>
						variant === 'regular' ? 400 : Number(variant.replace('italic', '')) || 400
					)
		);
		if (weights.length && !weights.includes(weight))
			throw new Error(`Font family "${family}" has no registered face for weight ${weight}.`);
	}
	return { ...font, ...(font.faces ? { faces: structuredClone(font.faces) } : {}) };
}

/** Prepare resource imports, fonts and styles before the editor makes its single undoable write. */
export function prepareDesignPresetBinding({
	settings,
	presetId,
	styles,
	mediaQuery,
	library,
	fontResolver,
}: {
	settings: StorySettingsType;
	presetId: string;
	styles: DesignPresetStyles;
	mediaQuery: StoryMediaQuery;
	library?: DesignDefinition;
	fontResolver?: FontResolver;
}) {
	const design = library
		? importDesignPreset(settings.design ?? createDesignDefinition(), library, presetId)
		: settings.design;
	const currentFonts = createFontResolver({ storyFonts: settings.fonts, googleFonts: [] });
	const resolved = new Map<string, ResolvedFont>();
	const validateFont = (family: string, weight?: number) => {
		const font = currentFonts.resolveExact(family) ?? fontResolver?.resolveExact(family);
		resolved.set(canonicalFontFamily(family), validateDesignPresetFontFace(font, family, weight));
	};
	const binding = planDesignPresetBinding({
		design,
		presetId,
		styles,
		mediaQuery,
		validateFont,
	});
	const fonts = registerResolvedFonts(settings.fonts ?? {}, [...resolved.values()]);
	return {
		...binding,
		settings: {
			...settings,
			...(design && design !== settings.design ? { design } : {}),
			...(resolved.size ? { fonts } : {}),
		},
	};
}
